import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";
import useIsMobile from "../../hooks/useIsMobile";

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

const combustibilLabel = {
  benzina: "Benzina",
  motorina: "Motorina",
  GPL: "GPL",
  electric: "Electric",
  hibrid: "Hibrid",
};

const ContractPreview = forwardRef(function ContractPreview({ data }, ref) {
  const isMobile = useIsMobile();
  const today = fmtDate(new Date());
  const dataVanzareFmt = data.dataVanzare ? fmtDate(new Date(data.dataVanzare)) : "___";

  const ciVanzatorFmt = !data.vanzatorPJ && data.vanzatorCI ? formatCI(data.vanzatorCI) : "___ ___";
  const ciCumparatorFmt = !data.cumparatorPJ && data.cumparatorCI ? formatCI(data.cumparatorCI) : "___ ___";

  const pret = Number(data.pretVanzare) || 0;
  const valuta = data.valuta || "RON";

  const sectionTitle = () => ({
    fontSize: "15px",
    fontWeight: 700,
    fontFamily: "'Playfair Display', serif",
    color: "#2d2418",
    margin: "28px 0 12px 0",
    paddingBottom: "6px",
    borderBottom: "1px solid #e8e0d4",
    letterSpacing: "0.03em",
    pageBreakAfter: "avoid",
    breakAfter: "avoid",
  });

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 8px 0",
    textAlign: "justify",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  const indent = { ...p, paddingLeft: isMobile ? "12px" : "20px" };
  const bold = { fontWeight: 700, color: "#2d2418" };
  const fieldRow = { ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" };


  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: isMobile ? "20px 16px" : "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      {/* Decorative top border */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #c45d3e, #a84832, #c45d3e)",
        borderRadius: "12px 12px 0 0",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "24px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}>
          Contract de Vanzare-Cumparare Auto
        </h2>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: "4px 0 0 0",
        }}>
          Nr. ___ din {today}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* I. PARTILE CONTRACTANTE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>I. PARTILE CONTRACTANTE</h3>

        <p style={p}><span style={bold}>1.1. Vanzatorul:</span></p>
        {data.vanzatorPJ ? (
          <>
            <p style={fieldRow}>Denumire: <span style={bold}>{data.vanzatorFirma || "________________________"}</span></p>
            <p style={fieldRow}>CUI: <span style={bold}>{data.vanzatorCUI || "________________________"}</span></p>
            {data.vanzatorRegCom && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data.vanzatorRegCom}</span></p>}
            <p style={fieldRow}>Sediu social: <span style={bold}>{data.vanzatorSediu || "________________________"}</span></p>
            <p style={fieldRow}>reprezentata prin <span style={bold}>{data.vanzatorReprezentant || "________________________"}</span></p>
          </>
        ) : (
          <>
            <p style={fieldRow}>Nume: <span style={bold}>{data.vanzatorNume || "________________________"}</span></p>
            <p style={fieldRow}>CNP: <span style={bold}>{data.vanzatorCNP || "________________________"}</span></p>
            <p style={fieldRow}>CI seria <span style={bold}>{ciVanzatorFmt}</span></p>
            <p style={fieldRow}>Domiciliul: <span style={bold}>{data.vanzatorAdresa || "________________________"}</span></p>
          </>
        )}

        <p style={{ ...p, color: "#8a7e70", fontStyle: "italic", margin: "16px 0" }}>si</p>

        <p style={p}><span style={bold}>1.2. Cumparatorul:</span></p>
        {data.cumparatorPJ ? (
          <>
            <p style={fieldRow}>Denumire: <span style={bold}>{data.cumparatorFirma || "________________________"}</span></p>
            <p style={fieldRow}>CUI: <span style={bold}>{data.cumparatorCUI || "________________________"}</span></p>
            {data.cumparatorRegCom && <p style={fieldRow}>Nr. Reg. Com.: <span style={bold}>{data.cumparatorRegCom}</span></p>}
            <p style={fieldRow}>Sediu social: <span style={bold}>{data.cumparatorSediu || "________________________"}</span></p>
            <p style={fieldRow}>reprezentata prin <span style={bold}>{data.cumparatorReprezentant || "________________________"}</span></p>
          </>
        ) : (
          <>
            <p style={fieldRow}>Nume: <span style={bold}>{data.cumparatorNume || "________________________"}</span></p>
            <p style={fieldRow}>CNP: <span style={bold}>{data.cumparatorCNP || "________________________"}</span></p>
            <p style={fieldRow}>CI seria <span style={bold}>{ciCumparatorFmt}</span></p>
            <p style={fieldRow}>Domiciliul: <span style={bold}>{data.cumparatorAdresa || "________________________"}</span></p>
          </>
        )}

        <p style={{ ...p, fontStyle: "italic", marginTop: "16px" }}>
          au convenit incheierea prezentului contract de vanzare-cumparare, in conformitate cu dispozitiile Codului Civil, in urmatoarele conditii:
        </p>
      </div>

      {/* II. OBIECTUL CONTRACTULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>II. OBIECTUL CONTRACTULUI</h3>
        <p style={p}>
          <span style={bold}>2.1.</span> Vanzatorul vinde si transmite dreptul de proprietate, iar Cumparatorul cumpara si dobandeste dreptul de proprietate asupra autovehiculului cu urmatoarele caracteristici:
        </p>
        <p style={fieldRow}>Marca: <span style={bold}>{data.marca || "________________________"}</span></p>
        <p style={fieldRow}>Model: <span style={bold}>{data.model || "________________________"}</span></p>
        <p style={fieldRow}>An fabricatie: <span style={bold}>{data.anFabricatie || "________________________"}</span></p>
        <p style={fieldRow}>Nr. inmatriculare: <span style={bold}>{data.nrInmatriculare || "________________________"}</span></p>
        <p style={fieldRow}>Serie sasiu (VIN): <span style={bold}>{data.serieSasiu || "________________________"}</span></p>
        {data.nrMotor && <p style={fieldRow}>Nr. motor: <span style={bold}>{data.nrMotor}</span></p>}
        {data.culoare && <p style={fieldRow}>Culoare: <span style={bold}>{data.culoare}</span></p>}
        <p style={fieldRow}>Combustibil: <span style={bold}>{combustibilLabel[data.combustibil] || data.combustibil}</span></p>
        {data.capacitateCilindrica && <p style={fieldRow}>Capacitate cilindrica: <span style={bold}>{data.capacitateCilindrica} cmc</span></p>}
        {data.kmRulati && <p style={fieldRow}>Kilometri rulati: <span style={bold}>{Number(data.kmRulati).toLocaleString("ro-RO")} km</span>{data.kmDeclaratie === "estimativ" ? " (estimativ)" : data.kmDeclaratie === "necunoscut" ? " (necunoscut)" : ""}</p>}
      </div>

      {/* III. PRETUL SI MODALITATEA DE PLATA */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>III. PRETUL SI MODALITATEA DE PLATA</h3>
        <p style={p}>
          <span style={bold}>3.1.</span> Pretul vanzarii este de <span style={bold}>{pret > 0 ? pret.toLocaleString("ro-RO") : "___"} {valuta}</span>{valuta !== "RON" ? <>, platibil in lei la cursul BNR din data platii</> : ""}, pe care Cumparatorul se obliga sa il achite Vanzatorului.
        </p>
        {(() => {
          let nr = 2;
          return (
            <>
              {data.modalitatePlata === "numerar + transfer" ? (
                <>
                  <p style={p}>
                    <span style={bold}>3.{nr++}.</span> Pretul se achita partial in numerar, in suma de <span style={bold}>{data.sumaNumerar ? Number(data.sumaNumerar).toLocaleString("ro-RO") : "___"} lei</span>, si partial prin transfer bancar{valuta === "RON" ? <>, diferenta de <span style={bold}>{pret > 0 && Number(data.sumaNumerar) > 0 ? (pret - Number(data.sumaNumerar)).toLocaleString("ro-RO") : "___"} lei</span></> : ""} reprezentand restul de plata.
                  </p>
                  <p style={p}>
                    <span style={bold}>3.{nr++}.</span> Plata in numerar se face cu respectarea plafonului legal.
                  </p>
                </>
              ) : (
                <p style={p}>
                  <span style={bold}>3.{nr++}.</span> Plata se va efectua prin <span style={bold}>{data.modalitatePlata || "transfer bancar"}</span>, cu respectarea legislatiei in vigoare privind plafonul platilor in numerar.
                </p>
              )}
              <p style={p}>
                <span style={bold}>3.{nr++}.</span> Data vanzarii: <span style={bold}>{dataVanzareFmt}</span>.
              </p>
              <p style={p}>
                <span style={bold}>3.{nr++}.</span> {data.pretAchitatLaSemnare ? "Vanzatorul declara ca a primit de la Cumparator suma mentionata la art. 3.1, pretul fiind achitat integral la data semnarii prezentului contract." : "Pretul urmeaza a fi achitat conform modalitatii de plata convenite, cel tarziu la data predarii autovehiculului, daca partile nu convin altfel in scris."}
              </p>
              <p style={p}>
                <span style={bold}>3.{nr}.</span> Predarea efectiva a autovehiculului si a tuturor documentelor aferente (certificat de inmatriculare, cartea de identitate a vehiculului, chei, ITP, RCA) se face la data de <span style={bold}>{dataVanzareFmt}</span>, la momentul semnarii prezentului contract.
              </p>
            </>
          );
        })()}
      </div>

      {/* IV. STAREA TEHNICA A AUTOVEHICULULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>IV. STAREA TEHNICA A AUTOVEHICULULUI</h3>
        <p style={p}>
          <span style={bold}>4.1.</span> Vanzatorul declara ca, la data vanzarii, autovehiculul se afla intr-o stare tehnica <span style={bold}>{data.stareTehnica || "buna"}</span>, corespunzatoare utilizarii normale, conform evaluarii sale.
        </p>
        <p style={p}>
          <span style={bold}>4.2.</span> {data.itpValid ? "Autovehiculul are inspectia tehnica periodica valabila la data vanzarii." : "Cumparatorul ia act ca autovehiculul nu are inspectia tehnica periodica valabila."}
        </p>
        <p style={p}>
          <span style={bold}>4.3.</span> {data.rcaValid ? "Autovehiculul are asigurare RCA valabila la data vanzarii." : "Cumparatorul ia act ca autovehiculul nu are asigurare RCA valabila."}
        </p>
        <p style={p}>
          <span style={bold}>4.4.</span> {data.liberSarcini ? "Vanzatorul declara ca autovehiculul este liber de orice sarcini, gajuri, sechestre sau alte drepturi ale tertilor." : "Cumparatorul a fost informat despre existenta urmatoarelor sarcini: _________________________."}
        </p>
        {(() => {
          let nr = 5;
          return (
            <>
              {data.kmDeclaratie === "real" && data.kmRulati && (
                <p style={p}>
                  <span style={bold}>4.{nr++}.</span> Vanzatorul declara pe propria raspundere ca kilometrajul indicat de <span style={bold}>{Number(data.kmRulati).toLocaleString("ro-RO")} km</span> este real si corespunde starii efective a autovehiculului.
                </p>
              )}
              {data.kmDeclaratie === "estimativ" && (
                <p style={p}>
                  <span style={bold}>4.{nr++}.</span> Vanzatorul declara ca kilometrajul afisat{data.kmRulati ? <> de <span style={bold}>{Number(data.kmRulati).toLocaleString("ro-RO")} km</span></> : ""} are caracter <span style={bold}>estimativ</span> si nu este garantat ca fiind real.
                </p>
              )}
              {data.kmDeclaratie === "necunoscut" && (
                <p style={p}>
                  <span style={bold}>4.{nr++}.</span> Vanzatorul declara ca nu poate garanta caracterul real al kilometrajului afisat, acesta fiind <span style={bold}>necunoscut</span>.
                </p>
              )}
              <p style={p}>
                <span style={bold}>4.{nr++}.</span> Vanzatorul declara ca nu a modificat si nu a dispus modificarea kilometrajului autovehiculului.
              </p>
              <p style={p}>
                <span style={bold}>4.{nr}.</span> Cumparatorul declara ca a inspectat autovehiculul si il accepta in starea in care se afla la momentul vanzarii (<span style={bold}>vandut ca atare</span>). Vanzatorul a adus la cunostinta Cumparatorului toate informatiile relevante privind starea tehnica a autovehiculului.
              </p>
            </>
          );
        })()}
      </div>

      {/* V. OBLIGATIILE VANZATORULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>V. OBLIGATIILE VANZATORULUI</h3>
        {[
          "Sa predea Cumparatorului autovehiculul descris la art. II, in starea tehnica constatata de ambele parti.",
          "Sa predea toate documentele aferente autovehiculului: certificat de inmatriculare, cartea de identitate a vehiculului, chei, ITP, RCA si orice alte documente necesare.",
          "Sa garanteze Cumparatorul impotriva evictiunii totale sau partiale, conform dispozitiilor Codului Civil.",
          "Sa declare ca autovehiculul nu face obiectul niciunui litigiu si nu este sechestrat sau indisponibilizat.",
          `Sa declare ca autovehiculul ${data.liberSarcini ? "este liber de orice sarcini" : "are sarcinile comunicate Cumparatorului"}.`,
          "Sa radieze autovehiculul din evidenta fiscala in termen de 30 de zile de la data incheierii prezentului contract.",
        ].map((text, i) => (
          <p key={i} style={indent}>
            <span style={bold}>5.{i + 1}.</span> {text}
          </p>
        ))}
      </div>

      {/* VI. OBLIGATIILE CUMPARATORULUI */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>VI. OBLIGATIILE CUMPARATORULUI</h3>
        {[
          "Sa achite integral pretul convenit conform art. III din prezentul contract.",
          "Sa efectueze fomalitatile de transcriere/inmatriculare a autovehiculului pe numele sau, in termenul prevazut de lege.",
          "Sa incheie o asigurare obligatorie RCA pe numele sau, inainte de punerea in circulatie a autovehiculului.",
          "Sa preia autovehiculul la data si locul convenite.",
        ].map((text, i) => (
          <p key={i} style={indent}>
            <span style={bold}>6.{i + 1}.</span> {text}
          </p>
        ))}
      </div>

      {/* VII. GARANTIE (conditional) */}
      {data.garantieActiva && (
        <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
          <h3 style={sectionTitle()}>VII. GARANTIE</h3>
          <p style={p}>
            <span style={bold}>7.1.</span> Vanzatorul acorda Cumparatorului o garantie de <span style={bold}>{data.garantieLuni || "3"} {Number(data.garantieLuni) === 1 ? "luna" : "luni"}</span> de la data semnarii prezentului contract, pentru viciile ascunse ale autovehiculului.
          </p>
          <p style={p}>
            <span style={bold}>7.2.</span> Garantia acopera urmatoarele componente: <span style={bold}>{[
              data.garantieMotor && "motor",
              data.garantieCutie && "cutie de viteze",
              data.garantieFranare && "sistem de franare",
              data.garantieAlte && data.garantieAlteText?.trim(),
            ].filter(Boolean).join(", ") || "___"}</span>.
          </p>
          <p style={p}>
            <span style={bold}>7.3.</span> Garantia nu acopera uzura normala, consumabilele si defectele aparute ca urmare a utilizarii necorespunzatoare.
          </p>
          <p style={p}>
            <span style={bold}>7.4.</span> Garantia acordata este una limitata, strict pentru componentele mentionate mai sus, si nu reprezinta o garantie comerciala generala.
          </p>
          <p style={p}>
            <span style={bold}>7.5.</span> Raspunderea Vanzatorului pentru vicii ascunse este limitata la termenul si componentele pentru care s-a acordat garantia, in masura permisa de lege.
          </p>
          <p style={p}>
            <span style={bold}>7.6.</span> In perioada de garantie, Vanzatorul se obliga sa remedieze pe cheltuiala proprie defectiunile acoperite sau sa restituie o parte proportionala din pretul de vanzare.
          </p>
        </div>
      )}

      {/* VIII (or VII). RASPUNDERE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>{data.garantieActiva ? "VIII" : "VII"}. RASPUNDERE</h3>
        {data.garantieActiva ? (
          <>
            <p style={indent}>
              <span style={bold}>8.1.</span> Vanzatorul raspunde pentru viciile ascunse ale autovehiculului, in conformitate cu dispozitiile art. 1707-1714 din Codul Civil, daca acestea existau la momentul vanzarii si nu au fost aduse la cunostinta Cumparatorului.
            </p>
            <p style={indent}>
              <span style={bold}>8.2.</span> Cumparatorul este obligat sa verifice autovehiculul la momentul preluarii. Viciile aparente constatate la predare si nesemnalate in procesul de receptie se considera acceptate.
            </p>
            <p style={indent}>
              <span style={bold}>8.3.</span> In cazul descoperirii de vicii ascunse, Cumparatorul poate solicita reducerea pretului sau rezolutiunea contractului, conform legii.
            </p>
          </>
        ) : (
          <>
            <p style={indent}>
              <span style={bold}>7.1.</span> Vanzarea se face in starea in care autovehiculul se afla la momentul tranzactiei, cu excluderea garantiei pentru vicii ascunse, in masura permisa de lege.
            </p>
            <p style={indent}>
              <span style={bold}>7.2.</span> Cumparatorul declara ca a inspectat autovehiculul, ca a fost informat asupra starii tehnice a acestuia si ca il accepta in starea constatata (<span style={bold}>vandut ca atare</span>).
            </p>
            <p style={indent}>
              <span style={bold}>7.3.</span> Vanzatorul raspunde in continuare pentru viciile pe care le-a ascuns cu intentie sau pentru declaratiile false privind starea autovehiculului, conform art. 1712 din Codul Civil.
            </p>
          </>
        )}
      </div>

      {/* IX (or VIII). LITIGII */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>{data.garantieActiva ? "IX" : "VIII"}. LITIGII</h3>
        <p style={indent}>
          <span style={bold}>{data.garantieActiva ? "9" : "8"}.1.</span> Litigiile decurgand din executarea prezentului contract se solutioneaza pe cale amiabila, iar in caz contrar de catre instantele judecatoresti competente.
        </p>
      </div>

      {/* X (or IX). DISPOZITII FINALE */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <h3 style={sectionTitle()}>{data.garantieActiva ? "X" : "IX"}. DISPOZITII FINALE</h3>
        <p style={indent}><span style={bold}>{data.garantieActiva ? "10" : "9"}.1.</span> Prezentul contract constituie titlu de proprietate si poate fi utilizat pentru transcrierea autovehiculului in evidentele autoritatilor competente.</p>
        <p style={indent}><span style={bold}>{data.garantieActiva ? "10" : "9"}.2.</span> Orice modificare a prezentului contract se face prin act aditional semnat de ambele parti.</p>
        <p style={indent}><span style={bold}>{data.garantieActiva ? "10" : "9"}.3.</span> Cheltuielile privind transcrierea autovehiculului si taxele aferente sunt suportate de Cumparator.</p>
        <p style={indent}><span style={bold}>{data.garantieActiva ? "10" : "9"}.4.</span> Prezentul contract reprezinta vointa partilor si inlocuieste orice intelegere anterioara, verbala sau scrisa.</p>
        <p style={indent}><span style={bold}>{data.garantieActiva ? "10" : "9"}.5.</span> Contractul se incheie in 2 (doua) exemplare originale, cate unul pentru fiecare parte{data.martori ? ", plus cate un exemplar pentru fiecare martor" : ""}.</p>
      </div>

      {/* Signatures */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: "48px",
        paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        pageBreakInside: "avoid",
        breakInside: "avoid",
        flexWrap: "wrap",
        gap: "24px",
      }}>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            VANZATOR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.vanzatorPJ
              ? (data.vanzatorFirma ? `${data.vanzatorFirma} prin ${data.vanzatorReprezentant || ""}` : "")
              : (data.vanzatorNume || "")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
        <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            CUMPARATOR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.cumparatorPJ
              ? (data.cumparatorFirma ? `${data.cumparatorFirma} prin ${data.cumparatorReprezentant || ""}` : "")
              : (data.cumparatorNume || "")}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
      </div>

      {/* Martori */}
      {data.martori && (
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "32px",
          paddingTop: "24px",
          borderTop: "1px dashed #d4cdc0",
          pageBreakInside: "avoid",
          breakInside: "avoid",
          flexWrap: "wrap",
          gap: "24px",
        }}>
          <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              MARTOR 1
            </p>
            <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "4px 0 0 0" }}>Nume: ________________________</p>
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "4px 0 0 0" }}>CNP: ________________________</p>
          </div>
          <div style={{ textAlign: "center", flex: isMobile ? "0 0 46%" : "0 0 40%" }}>
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              MARTOR 2
            </p>
            <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 20px 8px" }} />
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "4px 0 0 0" }}>Nume: ________________________</p>
            <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "4px 0 0 0" }}>CNP: ________________________</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default ContractPreview;
